import { PropsWithChildren, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';

import { Permission } from '@/@types/permission';
import { NotFoundPage } from '@/components/common/NotFoundPage';
import { Loader } from '@/components/layout/Loader/Loader';
import { AuthContext } from '@/contexts/AuthContext';
import { RoutePermissionOptions } from '@/modules/core/auth/types/route-access';
import { validateUserPermissions } from '@/modules/core/auth/utils/validateUserPermissions';

type ProtectedRouteProps = PropsWithChildren<RoutePermissionOptions>;

export function ProtectedRoute({
  children,
  permissions,
  strategy = 'every',
}: ProtectedRouteProps) {
  const router = useRouter();
  const { user, isAuthLoading } = useContext(AuthContext);

  useEffect(() => {
    if (!isAuthLoading && !user) {
      router.replace('/');
    }
  }, [isAuthLoading, router, user]);

  if (isAuthLoading || !user) {
    return <Loader />;
  }

  const userPermissions =
    user.role?.permissions.map((permission: Permission) => permission.name) ??
    [];
  const canAccessPage = validateUserPermissions({
    user: { is_admin: user.is_admin, permissions: userPermissions },
    permissions,
    strategy,
  });

  if (!canAccessPage) {
    return <NotFoundPage />;
  }

  return <>{children}</>;
}
